import { Slot } from '@radix-ui/react-slot';
import { LoadingCircleIconV2 } from '@repo/icons';
import { cn } from '@repo/utils/cn';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

const tabItemVariants = cva(
  'vt-font-weight-bold vt-text-size-medium sm:vt-text-size-medium-sm rounded-[0.25rem] items-center flex z-1 whitespace-nowrap focus-visible:outline-none ring-inherit disabled:cursor-not-allowed duration-300 transition-all cursor-pointer justify-center',
  {
    variants: {
      variant: {
        primary:
          'vt-tab-item-primary-text disabled:vt-tab-item-primary-text-disabled disabled:vt-tab-item-primary-bg-disabled active:vt-tab-item-primary-bg-active active:vt-tab-item-primary-text-active vt-tab-item-primary-bg hover:vt-tab-item-primary-text-hover hover:vt-tab-item-primary-bg-hover active:hover:vt-tab-item-primary-bg-active active:hover:vt-tab-item-primary-text-active',
        secondary:
          'vt-tab-item-secondary-bg hover:vt-tab-item-secondary-bg-hover disabled:vt-tab-item-secondary-bg-disabled active:vt-tab-item-secondary-bg-active border vt-tab-item-secondary-stroke hover:vt-tab-item-secondary-stroke-hover disabled:vt-tab-item-secondary-stroke-disabled active:vt-tab-item-secondary-stroke-active vt-tab-item-secondary-text hover:vt-tab-item-secondary-text-hover disabled:vt-tab-item-secondary-text-disabled active:vt-tab-item-secondary-text-active active:hover:vt-tab-item-secondary-bg-active active:hover:vt-tab-item-secondary-stroke-active active:hover:vt-tab-item-secondary-text-active',
        attached:
          'vt-tab-item-attached-bg hover:vt-tab-item-attached-bg-hover active:vt-tab-item-attached-bg-active disabled:vt-tab-item-attached-disabled vt-tab-item-attached-stroke hover:vt-tab-item-attached-stroke-hover active:vt-tab-item-attached-stroke-active disabled:vt-tab-item-attached-stroke-disabled vt-tab-item-attached-text hover:vt-tab-item-attached-text-hover active:vt-tab-item-attached-text-active disabled:vt-tab-item-attached-text-disabled rounded-none active:hover:vt-tab-item-attached-bg-active active:hover:vt-tab-item-attached-stroke-active active:hover:vt-tab-item-attached-text-active',
        tertiary:
          'vt-tab-item-tertiary-bg hover:vt-tab-item-tertiary-bg-hover disabled:vt-tab-item-tertiary-bg-disabled active:vt-tab-item-tertiary-bg-active border vt-tab-item-tertiary-stroke hover:vt-tab-item-tertiary-stroke-hover disabled:vt-tab-item-tertiary-stroke-disabled active:vt-tab-item-tertiary-stroke-active vt-tab-item-tertiary-text hover:vt-tab-item-tertiary-text-hover disabled:vt-tab-item-tertiary-text-disabled active:vt-tab-item-tertiary-text-active active:hover:vt-tab-item-tertiary-bg-active active:hover:vt-tab-item-tertiary-stroke-active active:hover:vt-tab-item-tertiary-text-active',
        inverted:
          'vt-tab-item-inverted-bg hover:vt-tab-item-inverted-bg-hover disabled:vt-tab-item-inverted-bg-disabled active:vt-tab-item-inverted-bg-active border vt-tab-item-inverted-stroke hover:vt-tab-item-inverted-stroke-hover disabled:vt-tab-item-inverted-stroke-disabled active:vt-tab-item-inverted-stroke-active vt-tab-item-inverted-text hover:vt-tab-item-inverted-text-hover disabled:vt-tab-item-inverted-text-disabled active:vt-tab-item-inverted-text-active active:hover:vt-tab-item-inverted-bg-active active:hover:vt-tab-item-inverted-stroke-active active:hover:vt-tab-item-inverted-text-active',
        blank:
          'rounded-none vt-tab-item-blank-bg hover:vt-tab-item-blank-bg-hover disabled:vt-tab-item-blank-bg-disabled active:vt-tab-item-blank-bg-active border vt-tab-item-blank-stroke hover:vt-tab-item-blank-stroke-hover disabled:vt-tab-item-blank-stroke-disabled active:vt-tab-item-blank-stroke-active vt-tab-item-blank-text hover:vt-tab-item-blank-text-hover disabled:vt-tab-item-blank-text-disabled active:vt-tab-item-blank-text-active active:hover:vt-tab-item-blank-bg-active active:hover:vt-tab-item-blank-stroke-active active:hover:vt-tab-item-blank-text-active',
        gray: 'rounded-none vt-tab-item-gray-bg hover:vt-tab-item-gray-bg-hover disabled:vt-tab-item-gray-bg-disabled active:vt-tab-item-gray-bg-active vt-tab-item-gray-stroke hover:vt-tab-item-gray-stroke-hover disabled:vt-tab-item-gray-stroke-disabled active:vt-tab-item-gray-stroke-active vt-tab-item-gray-text hover:vt-tab-item-gray-text-hover disabled:vt-tab-item-gray-text-disabled active:vt-tab-item-gray-text-active active:hover:vt-tab-item-gray-bg-active active:hover:vt-tab-item-gray-stroke-active active:hover:vt-tab-item-gray-text-active',
      },
      orientation: {
        vertical: 'min-h-[50px] min-w-[50px] pr-[10px] pl-[10px] flex-col justify-center space-y-1',
        horizontal: 'min-h-[40px] pr-[15px] pl-[15px] flex-row justify-center space-x-1 rtl:space-x-reverse',
      },
    },
    defaultVariants: {
      variant: 'primary',
      orientation: 'horizontal',
    },
  }
);

export interface TabItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof tabItemVariants> {
  loading?: boolean;
  isActive?: boolean;
  asChild?: boolean;
}

const getActiveClasses = (variant: TabItemProps['variant'], isActive?: boolean) => {
  if (!isActive) return '';

  switch (variant) {
    case 'primary':
      return 'vt-tab-item-primary-bg-active vt-tab-item-primary-text-active';
    case 'secondary':
      return 'vt-tab-item-secondary-bg-active vt-tab-item-secondary-text-active vt-tab-item-secondary-stroke-active';
    case 'attached':
      return 'vt-tab-item-attached-stroke-active vt-tab-item-attached-bg-active vt-tab-item-attached-text-active hover:vt-tab-item-attached-stroke-active hover:vt-tab-item-attached-bg-active hover:vt-tab-item-attached-text-active';
    case 'tertiary':
      return 'vt-tab-item-tertiary-bg-active vt-tab-item-tertiary-text-active vt-tab-item-tertiary-stroke-active';
    case 'inverted':
      return 'vt-tab-item-inverted-bg-active vt-tab-item-inverted-text-active vt-tab-item-inverted-stroke-active';
    case 'blank':
      return 'vt-tab-item-blank-bg-active vt-tab-item-blank-text-active vt-tab-item-blank-stroke-active';
    case 'gray':
      return 'vt-tab-item-gray-bg-active vt-tab-item-gray-text-active vt-tab-item-gray-stroke-active';
    default:
      return '';
  }
};

const TabItem = React.forwardRef<HTMLButtonElement, TabItemProps>(
  (
    { className, isActive, asChild, variant = 'primary', orientation = 'horizontal', children, loading, ...props },
    ref
  ) => {
    const activeClasses = getActiveClasses(variant, isActive);
    const disabled = loading || isActive;

    if (asChild) {
      const { onClick, ...slotProps } = props as any;
      return (
        <Slot
          ref={ref}
          className={cn(
            tabItemVariants({ variant, orientation }),
            activeClasses,
            disabled && 'pointer-events-none',
            className
          )}
          aria-disabled={disabled}
          {...slotProps}
          onClick={(e: React.MouseEvent) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            onClick?.(e);
          }}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={cn(
          tabItemVariants({ variant, orientation }),
          activeClasses,
          disabled && 'pointer-events-none',
          className
        )}
        aria-disabled={disabled}
        disabled={disabled}
        {...props}
      >
        <div className="relative z-[2] w-full">
          <div
            className={cn(
              { invisible: loading },
              orientation === 'vertical'
                ? 'flex flex-col items-center justify-center space-y-1'
                : 'flex flex-row items-center justify-center space-x-1'
            )}
          >
            {children}
          </div>
          {loading && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <LoadingCircleIconV2 className="size-7 animate-spin duration-700" />
            </span>
          )}
        </div>
      </button>
    );
  }
);
TabItem.displayName = 'TabItem';

export { TabItem, tabItemVariants };
